import React, { useState, useEffect } from 'react';
import Calling from '../../../api/Calling';
import { toast } from 'react-toastify';
import MainButton from '../../../components/ul/MainButton';
import { FiFilter, FiRefreshCw, FiCalendar, FiEye, FiMousePointer, FiClock } from 'react-icons/fi';
import { FaEye, FaEdit } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const InhouseDashboard = () => {
    const [loader, setLoader] = useState(false);
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [selectedBtn, setSelectedBtn] = useState('today');
    const [selectShowType, setSelectShowType] = useState('all');
    const [adsData, setAdsData] = useState([]);
    const [lastUpdated, setLastUpdated] = useState('Just now');
    const [sortConfig, setSortConfig] = useState({ key: 'view', direction: 'desc' });
    const navigate = useNavigate();

    const fetchData = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getInHouseAdsDashboard(startDate, endDate);
            if (resp?.status) {
                setAdsData(resp.data || []);
                setLastUpdated(new Date().toLocaleTimeString());
            } else {
                toast.error(resp?.message || 'Failed to fetch data');
                setAdsData([]);
            }
        } catch (error) {
            console.log(error);
            toast.error('Error fetching dashboard data');
            setAdsData([]);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [startDate, endDate, selectShowType]);

    const handleDateRangeFilter = (range) => {
        const today = new Date();
        let start, end;

        switch (range) {
            case 'today':
                start = today.toISOString().split('T')[0];
                end = start;
                setSelectedBtn('today');
                break;
            case 'yesterday':
                const yesterday = new Date();
                yesterday.setDate(today.getDate() - 1);
                start = yesterday.toISOString().split('T')[0];
                end = start;
                setSelectedBtn('yesterday');
                break;
            case 'thisWeek':
                const firstDayOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
                const lastDayOfWeek = new Date(firstDayOfWeek);
                lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
                start = firstDayOfWeek.toISOString().split('T')[0];
                end = lastDayOfWeek.toISOString().split('T')[0];
                setSelectedBtn('thisWeek');
                break;
            case 'thisMonth':
                start = new Date(today.getFullYear(), today.getMonth(), 1)
                    .toISOString()
                    .split('T')[0];
                end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
                    .toISOString()
                    .split('T')[0];
                setSelectedBtn('thisMonth');
                break;
            case 'last6Months':
                start = new Date(today.getFullYear(), today.getMonth() - 5, 1)
                    .toISOString()
                    .split('T')[0];
                end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
                    .toISOString()
                    .split('T')[0];
                setSelectedBtn('last6Months');
                break;
            case 'thisYear':
                start = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
                end = new Date(today.getFullYear(), 11, 31).toISOString().split('T')[0];
                setSelectedBtn('thisYear');
                break;
            case 'lastYear':
            default:
                start = '';
                end = '';
                setSelectedBtn('range');
        }

        setStartDate(start);
        setEndDate(end);
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchData();
    };

    const handleFormDate = (e) => {
        if (e.target.name === "startDate") {
            setStartDate(e.target.value);
            setSelectedBtn('range');
        } else {
            setEndDate(e.target.value);
            setSelectedBtn('range');
        }
    };

    const handleDataTypeChange = (type) => {
        setSelectShowType(type);
    };

    const handleRefresh = () => {
        fetchData();
    };

    // Sorting function
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Get filtered data based on selectShowType
    const getFilteredData = () => {
        let filteredData = [...adsData];

        if (selectShowType === 'all') {
            return filteredData;
        } else if (selectShowType === 'ntv') {
            return filteredData.filter(ad => ad.adType === 'ntv');
        } else if (selectShowType === 'fs') {
            return filteredData.filter(ad => ad.adType === 'fs');
        }

        return filteredData;
    };

    // Get sorted data
    const getSortedData = () => {
        const filteredData = getFilteredData();

        return [...filteredData].sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (a[sortConfig.key] > b[sortConfig.key]) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    // Format date for display
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Get total views and clicks by type
    const getTotalViewsByType = (type = null) => {
        if (type) {
            return adsData.filter(ad => ad.adType === type).reduce((total, ad) => total + ad.view, 0);
        }
        return getFilteredData().reduce((total, ad) => total + ad.view, 0);
    };

    const getTotalClicksByType = (type = null) => {
        if (type) {
            return adsData.filter(ad => ad.adType === type).reduce((total, ad) => total + ad.clickCount, 0);
        }
        return getFilteredData().reduce((total, ad) => total + ad.clickCount, 0);
    };

    const getActiveAdsByType = (type = null) => {
        if (type) {
            return adsData.filter(ad => ad.adType === type && ad.status).length;
        }
        return getFilteredData().filter(ad => ad.status).length;
    };

    const handleEditInHouseAds = (id) => {
        navigate(`/manage/ads/inHouseAds/edit/${id}`);
    };

    const handleViewAnalytics = (id) => {
        navigate(`/views/dashboard/${id}`);
    };

    // Format view and click numbers
    const formatView = (count) => {
        if (count >= 1000000) {
            return (count / 1000000).toFixed(1) + 'M';
        } else if (count >= 1000) {
            return (count / 1000).toFixed(1) + 'K';
        }
        return count;
    };

    // Stats summary component with separate NTV and FS metrics
    const StatsSummary = () => {
        return (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                {/* Total Views Card */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-main-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-main-subfont text-sm">Total Views</p>
                            <h3 className="text-2xl font-bold text-main-font mt-1">{getTotalViewsByType()}</h3>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-full">
                            <FiEye className="text-main-blue text-xl" />
                        </div>
                    </div>
                </div>

                {/* NTV Views Card */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-main-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-main-subfont text-sm">NTV Views</p>
                            <h3 className="text-2xl font-bold text-blue-600 mt-1">{getTotalViewsByType('ntv')}</h3>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-full">
                            <FiEye className="text-blue-600 text-xl" />
                        </div>
                    </div>
                </div>

                {/* FS Views Card */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-main-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-main-subfont text-sm">FS Views</p>
                            <h3 className="text-2xl font-bold text-purple-600 mt-1">{getTotalViewsByType('fs')}</h3>
                        </div>
                        <div className="p-3 bg-purple-100 rounded-full">
                            <FiEye className="text-purple-600 text-xl" />
                        </div>
                    </div>
                </div>

                {/* Total Clicks Card */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-main-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-main-subfont text-sm">Total Clicks</p>
                            <h3 className="text-2xl font-bold text-main-font mt-1">{getTotalClicksByType()}</h3>
                        </div>
                        <div className="p-3 bg-green-100 rounded-full">
                            <FiMousePointer className="text-green-600 text-xl" />
                        </div>
                    </div>
                </div>

                {/* Active Ads Card */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-main-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-main-subfont text-sm">Active Ads</p>
                            <h3 className="text-2xl font-bold text-main-font mt-1">
                                {getActiveAdsByType()}
                            </h3>
                        </div>
                        <div className="p-3 bg-purple-100 rounded-full">
                            <FiClock className="text-purple-600 text-xl" />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Secondary Stats with NTV and FS clicks
    const SecondaryStats = () => {
        return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* NTV Clicks Card */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-main-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-main-subfont text-sm">NTV Clicks</p>
                            <h3 className="text-2xl font-bold text-blue-600 mt-1">{getTotalClicksByType('ntv')}</h3>
                            <p className="text-xs text-main-subfont mt-1">
                                CTR: {getTotalViewsByType('ntv') > 0
                                    ? ((getTotalClicksByType('ntv') / getTotalViewsByType('ntv')) * 100).toFixed(2)
                                    : 0}%
                            </p>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-full">
                            <FiMousePointer className="text-blue-600 text-xl" />
                        </div>
                    </div>
                </div>

                {/* FS Clicks Card */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-main-border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-main-subfont text-sm">FS Clicks</p>
                            <h3 className="text-2xl font-bold text-purple-600 mt-1">{getTotalClicksByType('fs')}</h3>
                            <p className="text-xs text-main-subfont mt-1">
                                CTR: {getTotalViewsByType('fs') > 0
                                    ? ((getTotalClicksByType('fs') / getTotalViewsByType('fs')) * 100).toFixed(2)
                                    : 0}%
                            </p>
                        </div>
                        <div className="p-3 bg-purple-100 rounded-full">
                            <FiMousePointer className="text-purple-600 text-xl" />
                        </div>
                    </div>
                </div>

                {/* Active Ads by Type */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-main-border">
                    <div className="flex items-center justify-between">
                        <div className="w-full">
                            <p className="text-main-subfont text-sm mb-2">Active Ads by Type</p>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-blue-600 font-medium">NTV:</span>
                                <span className="text-main-font font-bold">{getActiveAdsByType('ntv')}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-purple-600 font-medium">FS:</span>
                                <span className="text-main-font font-bold">{getActiveAdsByType('fs')}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className='w-full text-main-font mt-16 px-2 md:px-3 md:ml-[4.4rem]'>
            <div className="flex items-center justify-between pt-6 pb-2">
                <h1 className="text-2xl font-bold text-main-font">In-House Ads Dashboard</h1>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleRefresh}
                        className="bg-main-blue hover:bg-opacity-90 text-white p-2 rounded-full shadow-sm transition-colors duration-200"
                        title="Refresh data"
                        disabled={loader}
                    >
                        <FiRefreshCw size={18} className={loader ? "animate-spin" : ""} />
                    </button>
                    <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-main-border text-sm text-main-subfont flex items-center">
                        <span className="mr-2">Last updated:</span>
                        <span className="font-medium text-main-font">{lastUpdated}</span>
                    </div>
                </div>
            </div>

            {/* Tab Bar for Filtering */}
            <div className="bg-white rounded-xl shadow-sm border border-main-border p-3 mb-6">
                <div className="flex space-x-4">
                    <button
                        onClick={() => handleDataTypeChange('all')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${selectShowType === 'all'
                                ? 'bg-main-blue text-white shadow-sm'
                                : 'text-main-subfont hover:bg-gray-100'
                            }`}
                    >
                        All Ads
                    </button>
                    <button
                        onClick={() => handleDataTypeChange('ntv')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${selectShowType === 'ntv'
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'text-main-subfont hover:bg-gray-100'
                            }`}
                    >
                        Native Ads
                    </button>
                    <button
                        onClick={() => handleDataTypeChange('fs')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${selectShowType === 'fs'
                                ? 'bg-purple-600 text-white shadow-sm'
                                : 'text-main-subfont hover:bg-gray-100'
                            }`}
                    >
                        Full Screen Ads
                    </button>
                </div>
            </div>

            {/* Stats Summary */}
            <StatsSummary />

            {/* Secondary Stats with NTV and FS clicks */}
            <SecondaryStats />

            {/* Date Range Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-main-border overflow-hidden mb-6">
                <div className="p-6">
                    <div className="flex items-center mb-4">
                        <FiCalendar className="text-main-blue mr-2" />
                        <h2 className="text-lg font-semibold text-main-font">Date Range</h2>
                    </div>
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12 md:col-span-8 flex flex-wrap gap-3">
                            <MainButton
                                onClick={() => handleDateRangeFilter('today')}
                                label={'Today'}
                                selectState={selectedBtn === "today"}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('yesterday')}
                                label={'Yesterday'}
                                selectState={selectedBtn === "yesterday"}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('thisWeek')}
                                label={'This Week'}
                                selectState={selectedBtn === "thisWeek"}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('thisMonth')}
                                label={'This Month'}
                                selectState={selectedBtn === "thisMonth"}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('last6Months')}
                                label={'Last 6 Months'}
                                selectState={selectedBtn === "last6Months"}
                            />
                            <MainButton
                                onClick={() => handleDateRangeFilter('thisYear')}
                                label={'This Year'}
                                selectState={selectedBtn === "thisYear"}
                            />
                        </div>

                        {/* Date Range Form */}
                        <div className="col-span-12 md:col-span-4 flex justify-end">
                            <form onSubmit={handleFilterSubmit} className="w-full md:w-auto">
                                <div className="grid grid-cols-12 gap-3 justify-items-end">
                                    <div className="col-span-6 relative">
                                        <input
                                            type="date"
                                            name="startDate"
                                            value={startDate}
                                            onChange={handleFormDate}
                                            min="2023-01-01"
                                            max={new Date().toISOString().split('T')[0]}
                                            className={`w-full px-4 py-2 rounded-lg shadow-sm border cursor-pointer bg-white ${selectedBtn === "range"
                                                ? 'border-main-blue ring-2 ring-main-th text-main-blue'
                                                : 'border-main-border text-main-font hover:border-main-blue'
                                                }`}
                                        />
                                    </div>
                                    <div className="col-span-6 relative">
                                        <input
                                            type="date"
                                            name="endDate"
                                            value={endDate}
                                            onChange={handleFormDate}
                                            min="2023-01-01"
                                            max={new Date().toISOString().split('T')[0]}
                                            className={`w-full px-4 py-2 rounded-lg shadow-sm border cursor-pointer bg-white ${selectedBtn === "range"
                                                ? 'border-main-blue ring-2 ring-main-th text-main-blue'
                                                : 'border-main-border text-main-font hover:border-main-blue'
                                                }`}
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-xl shadow-sm border border-main-border overflow-hidden">
                <div className="p-6 pb-3">
                    <h2 className="text-lg font-semibold text-main-font mb-4">In-House Ads Performance</h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-main-border">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-main-subfont uppercase tracking-wider">
                                        Ad Details
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-main-subfont uppercase tracking-wider cursor-pointer"
                                        onClick={() => handleSort('adType')}
                                    >
                                        Ad Type
                                        {sortConfig.key === 'adType' && (
                                            <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                        )}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-main-subfont uppercase tracking-wider cursor-pointer"
                                        onClick={() => handleSort('view')}
                                    >
                                        Performance
                                        {sortConfig.key === 'view' && (
                                            <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                        )}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-main-subfont uppercase tracking-wider"
                                    >
                                        CTA
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-main-subfont uppercase tracking-wider cursor-pointer"
                                        onClick={() => handleSort('web_stay_time')}
                                    >
                                        Stay Time (s)
                                        {sortConfig.key === 'web_stay_time' && (
                                            <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                        )}
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-main-subfont uppercase tracking-wider cursor-pointer"
                                        onClick={() => handleSort('createdAt')}
                                    >
                                        Created On
                                        {sortConfig.key === 'createdAt' && (
                                            <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                        )}
                                    </th>

                                    <th
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-main-subfont uppercase tracking-wider"
                                    >
                                        Status
                                    </th>

                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-main-border">
                                {loader ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-4 text-center text-main-subfont">
                                            Loading data...
                                        </td>
                                    </tr>
                                ) : getSortedData().length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-4 text-center text-main-subfont">
                                            No data available for the selected filters.
                                        </td>
                                    </tr>
                                ) : (
                                    getSortedData().map((ad) => (
                                        <tr key={ad._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-10 w-10">
                                                        <img className="h-10 w-10 rounded-md object-cover" src={ad.icon} alt={ad.mainTitle} />
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-main-font">{ad.mainTitle}</div>
                                                        <div className="text-xs text-main-subfont">{ad.subTitle}</div>
                                                        <div className="mt-2 flex space-x-2">
                                                            <button
                                                                onClick={() => handleEditInHouseAds(ad._id)}
                                                                className="px-2 py-1 bg-blue-50 text-main-blue text-xs rounded-md hover:bg-blue-100 transition-colors"
                                                            >
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={() => handleViewAnalytics(ad._id)}
                                                                className="px-2 py-1 bg-green-50 text-green-600 text-xs rounded-md hover:bg-green-100 transition-colors"
                                                            >
                                                                Analytics
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${ad.adType === 'ntv' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                                                    }`}>
                                                    {ad.adType === 'ntv' ? 'Native' : 'Full Screen'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-main-font">
                                                <div className="flex flex-col">
                                                    <div className="flex items-center mb-1">
                                                        <FaEye className="text-main-blue mr-2" size={14} />
                                                        <span
                                                            className="text-sm text-main-blue font-medium cursor-pointer hover:underline"
                                                            onClick={() => handleViewAnalytics(ad._id)}
                                                        >
                                                            {formatView(ad.view)} views
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center">
                                                        <svg className="text-green-500 mr-2" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M20 16L14 10L10 14L4 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M20 16H15V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                        <span
                                                            className="text-sm text-green-600 font-medium cursor-pointer hover:underline"
                                                            onClick={() => handleViewAnalytics(ad._id)}
                                                        >
                                                            {formatView(ad.clickCount)} clicks
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {ad.view > 0 && (
                                                    <span
                                                        className={`px-2 py-1 rounded-full text-sm  font-medium ${((ad.clickCount / ad.view) * 100) >= 20
                                                            ? "bg-green-100 text-green-700"
                                                            : "bg-yellow-100 text-yellow-700"
                                                            }`}
                                                    >
                                                        {((ad.clickCount / ad.view) * 100).toFixed(2)}%
                                                    </span>
                                                )}
                                            </td>


                                            <td className="px-6 py-4 text-sm text-main-font">
                                                {ad.web_stay_time}s
                                            </td>
                                            <td className="px-6 py-4 text-sm text-main-subfont">
                                                {formatDate(ad.createdAt)}
                                            </td>
                                            <td className="px-6 py-4 text-sm">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${ad.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    {ad.status ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InhouseDashboard;